import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Chip,
  IconButton,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
// Using simple HTML date inputs instead of MUI X date picker for compatibility
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';

// Import existing components
import MediaUpload from '../../Components/MediaUpload';
import CategoryChips from '../../Components/CategoryChips';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoCompletePlaces from '../../Components/AutoCompletePlaces';
import { request } from '../../Utils/request';
import { fetchCategories } from '../../services/CommonApiCall';
import { getSignedUploadUrls, uploadFileToSignedUrl } from '../../services/API/Api';
import { tripDraftUtils } from '../../Utils/tripDraftStorage';
import { TripDraft } from '../../store/slices/TripDraftSlice';
import { fileToBase64, base64ToFile } from '../../Utils/base64Converter';
import { revokeBlobUrls } from '../../Utils/blobUrlUtils';
import { rupeesToPaise } from '../../Utils/currencyConverter';
import { ReactComponent as DraftIcon } from "../../Icons/svg/draft.svg";

interface TaskFile {
  fileKey: string;
  fileType: string;
  featured: boolean;
}

interface DailyTask {
  placeRefId?: string;
  name?: string;
  file?: TaskFile;
  description: string;
}

interface DailySchedule {
  day: string;
  tasks: DailyTask[];
}

interface Schedule {
  dailySchedule: DailySchedule[];
}

interface Batch {
  from: string;
  to: string;
  groupSize: number;
  maxGroupSizeAllowed: number;
}

interface TripFile {
  fileKey: string;
  fileType: string;
  featured: boolean;
}

interface Location {
  lat: number;
  lng: number;
  name: string;
}

interface TripFormData {
  type: 'Trip' | 'Itinerary';
  title: string;
  description: string;
  schedule: Schedule;
  pricePerPerson: number;
  batches: Batch[];
  files: TripFile[];
  notes: string;
  category: string[];
  location: Location | null;
}

interface PreviewImage {
  url: string;
  caption: string;
  featured: boolean;
  file?: File;
  /** When making a copy, reuse existing file without upload */
  fileKey?: string;
  fileType?: string;
}



const CreateTrip: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const categories = useSelector((state: RootState) => state.category?.categories || []);
  const tripDrafts = useSelector((state: RootState) => state.tripDraft?.drafts || []);
  const user = useSelector((state: RootState) => state.user?.user);

  useEffect(() => {
    fetchCategories(dispatch, 'trip-itinerary');
  }, []);

  // Handle trip editing or make-a-copy from navigation state
  useEffect(() => {
    const trip = location.state?.trip;
    const isEdit = location.state?.isEdit;
    const isCopy = location.state?.isCopy;

    if (trip && (isEdit || isCopy)) {
      // Determine trip type: check tripType from state, then trip.type, default to 'Trip'
      const tripType = location.state.tripType ||
        (trip.type === 'Itinerary' || trip.type === 'itinerary' ? 'Itinerary' : 'Trip');

      if (isEdit) {
        // Store original trip data for change tracking (keep price in paise for comparison)
        setEditingTrip(trip);
        const originalData = {
          type: tripType,
          title: trip.title,
          description: trip.description || '',
          schedule: trip.schedule,
          pricePerPerson: trip.pricePerPerson, // Keep in paise for comparison
          batches: trip.batches || [],
          files: trip.files || [],
          notes: trip.notes || '',
          category: trip.category || [],
          location: trip.location,
        };
        setOriginalTripData(originalData);
        console.log('Original trip data stored for comparison:', originalData);
      }
      // For isCopy we do not set editingTrip or originalTripData — form is for a new trip

      // Convert price from paise to rupees for form display
      const priceInRupees = trip.pricePerPerson ? trip.pricePerPerson / 100 : 0;

      setFormData({
        type: tripType as 'Trip' | 'Itinerary',
        title: trip.title,
        description: trip.description || '',
        schedule: trip.schedule || { dailySchedule: [] },
        pricePerPerson: priceInRupees,
        batches: trip.batches || [],
        files: trip.files || [],
        notes: trip.notes || '',
        category: trip.category || [],
        location: trip.location || null,
      });

      setType(tripType);
      setSelectedCategories(trip.category || []);

      // Convert files to preview format; for copy include fileKey/fileType so submit can reuse without upload
      if (trip.files && trip.files.length > 0) {
        const previewImages = trip.files.map((file: any, index: number) => ({
          url: file.fileURL || '',
          caption: '',
          featured: file.featured ?? index === 0,
          fileKey: file.fileKey,
          fileType: file.fileType || 'image/jpeg',
        }));
        setImagesData(previewImages);
        setMediaUploadKey(prev => prev + 1);
      }
    }
  }, [location.state]);

  // Handle draft loading from navigation state
  // Load draft if draft exists and there's no trip (to distinguish from trip editing)
  useEffect(() => {
    if (location.state?.draft && !location.state?.trip) {
      const draft = location.state.draft;
      const isPreview = location.state.isPreview;

      if (draft) {
        setFormData({
          type: draft.type,
          title: draft.title,
          description: draft.description,
          schedule: draft.schedule,
          pricePerPerson: draft.pricePerPerson,
          batches: draft.batches,
          files: draft.files,
          notes: draft.notes,
          category: draft.category,
          location: draft.location,
        });

        // Update the type state to match the draft type
        setType(draft.type);
        setSelectedCategories(draft.category);
        setCurrentDraftId(draft.id);

        // Convert base64 images back to preview format
        if (draft.imagesData && draft.imagesData.length > 0) {
          const restoredImages = draft.imagesData.map((img: any) => {
            const newFile = img.fileBase64 ? base64ToFile(img.fileBase64, img.fileName, img.fileType) : null;
            const restoredImage = {
              url: newFile ? URL.createObjectURL(newFile) : img.url,
              caption: img.caption || '',
              featured: img.featured || false,
              file: newFile || undefined,
            };
            return restoredImage;
          });
          setImagesData(restoredImages);
          // Force MediaUpload component to re-render with new data
          setMediaUploadKey(prev => prev + 1);
        }

      }
    }
  }, [location.state]);

  const [formData, setFormData] = useState<TripFormData>({
    type: 'Trip',
    title: '',
    description: '',
    schedule: {
      dailySchedule: [
        {
          day: '1',
          tasks: [
            {
              name: '',
              description: '',
              placeRefId: ''
            }
          ]
        }
      ]
    },
    pricePerPerson: 0,
    batches: [
      {
        from: '',
        to: '',
        groupSize: 0,
        maxGroupSizeAllowed: 1
      }
    ],
    files: [],
    notes: '',
    category: [],
    location: null,
  });

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [type, setType] = useState<string>('Trip');
  // Local state to keep raw image previews (with File objects)
  const [imagesData, setImagesData] = useState<PreviewImage[]>([]);

  useEffect(() => {
    return () => {
      revokeBlobUrls(imagesData);
    };
  }, [imagesData]);

  // Draft-related state
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [mediaUploadKey, setMediaUploadKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Edit mode state
  const [editingTrip, setEditingTrip] = useState<any>(null);
  const isEditMode = Boolean(editingTrip);

  const [originalTripData, setOriginalTripData] = useState<any>(null);

  // Track which fields have been touched
  const [touchedFields, setTouchedFields] = useState<{ [key: string]: boolean }>({});

  // Track which day is currently selected in the Daily Schedule view
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);

  // Places data for autocomplete
  const [places, setPlaces] = useState<any[]>([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);

  const availableCategories = categories;

  // Helper function to mark a field as touched
  const markFieldTouched = (fieldName: string) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  // Helper: show validation only when the field is enabled (disabled fields never show errors)
  const shouldShowError = (fieldName: string, fieldDisabled?: boolean) => {
    if (fieldDisabled) return false;
    return touchedFields[fieldName];
  };

  const getTodayDateOnlyTime = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime();
  };

  const getBatchDateTime = (value?: string) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    date.setHours(0, 0, 0, 0);
    return date.getTime();
  };

  const validateTripForm = () => {
    const nextTouched: { [key: string]: boolean } = {
      title: true,
      description: true,
      category: true,
      location: true,
      pricePerPerson: true,
    };

    let hasError = false;

    if (imagesData.length === 0) hasError = true;
    if (!formData.type) hasError = true;
    if (!formData.title.trim()) hasError = true;
    if (formData.title.trim().length > 100) hasError = true;
    if (!formData.description.trim()) hasError = true;
    if (!formData.category || formData.category.length === 0) hasError = true;
    if (!formData.location || !formData.location.name) hasError = true;
    if (!formData.pricePerPerson || formData.pricePerPerson <= 0) hasError = true;

    if (type === 'Trip') {
      if (!formData.batches || formData.batches.length === 0) hasError = true;

      const todayDateTime = getTodayDateOnlyTime();

      formData.batches.forEach((batch, batchIndex) => {
        const fromKey = `batch_${batchIndex}_from`;
        const toKey = `batch_${batchIndex}_to`;
        const maxKey = `batch_${batchIndex}_maxGroupSize`;
        nextTouched[fromKey] = true;
        nextTouched[toKey] = true;
        nextTouched[maxKey] = true;

        const fromDateTime = getBatchDateTime(batch.from);
        const toDateTime = getBatchDateTime(batch.to);

        // Start date should not be older than today.
        if (!fromDateTime || fromDateTime < todayDateTime) hasError = true;

        // End date should be greater than or equal to start date.
        if (!toDateTime || (fromDateTime !== null && toDateTime < fromDateTime)) hasError = true;

        // Max group should be required, number-only, and not exceed 50.
        if (
          !Number.isFinite(batch.maxGroupSizeAllowed) ||
          batch.maxGroupSizeAllowed <= 0 ||
          batch.maxGroupSizeAllowed > 50
        ) {
          hasError = true;
        }
      });
    }

    formData.schedule.dailySchedule.forEach((day, dayIndex) => {
      if (!day.tasks || day.tasks.length === 0) {
        hasError = true;
      }
      day.tasks.forEach((task, taskIndex) => {
        const nameKey = `task_${dayIndex}_${taskIndex}_name`;
        const descriptionKey = `task_${dayIndex}_${taskIndex}_description`;
        nextTouched[nameKey] = true;
        nextTouched[descriptionKey] = true;

        // Either place selection or title is required.
        if (!task.placeRefId && !task.name?.trim()) hasError = true;
        if (!task.description?.trim()) hasError = true;
      });
    });

    setTouchedFields(prev => ({ ...prev, ...nextTouched }));

    return hasError;
  };

  // Fetch places from API
  useEffect(() => {
    const fetchPlaces = async () => {
      setLoadingPlaces(true);
      try {
        const response = await request<any>('/places', {
          method: 'GET',
          authorization: true,
        });

        if (response?.data) {
          // Format places for autocomplete
          const formattedPlaces = response.data.map((place: any) => ({
            label: place.name || place.title,
            placeRefId: place._id,
            ...place
          }));
          setPlaces(formattedPlaces);
        }
      } catch (error) {
        console.error('Error fetching places:', error);
      } finally {
        setLoadingPlaces(false);
      }
    };

    fetchPlaces();
  }, []);

  /** When navigating from "Make a copy", trip vs itinerary must stay the same as the source. */
  const lockTripTypeForCopy = location.state?.isCopy === true;

  const handleInputChange = (field: keyof TripFormData, value: any) => {
    if (field === 'type' && lockTripTypeForCopy) {
      return;
    }
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Also update type state when type field changes
    if (field === 'type') {
      setType(value);
    }
  };

  // Capture image previews (with File objects) but defer uploading until submit
  const handleFilesChange = (images: PreviewImage[]) => {
    setImagesData(images);
  };

  const handleLocationSelect = (location: { lat: number; lng: number; address: string } | null) => {
    if (!location) {
      setFormData(prev => ({ ...prev, location: null }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      location: {
        lat: location.lat,
        lng: location.lng,
        name: location.address
      }
    }));
  };

  const handleCategoriesChange = (categories: any[]) => {
    const categoryNames = categories.map(cat => cat.name);
    setFormData(prev => ({
      ...prev,
      category: categoryNames
    }));
    setSelectedCategories(categoryNames);
  };

  const addDay = () => {
    const newDayNumber = formData.schedule.dailySchedule.length + 1;
    const newDay: DailySchedule = {
      day: `${newDayNumber}`,
      tasks: [
        {
          name: '',
          description: '',
          placeRefId: ''
        }
      ]
    };
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: [...prev.schedule.dailySchedule, newDay]
      }
    }));

    // Automatically focus the newly added day
    setSelectedDayIndex(newDayNumber - 1);
  };

  const removeDay = (dayIndex: number) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: prev.schedule.dailySchedule
          .filter((_, index) => index !== dayIndex)
          .map((day, index) => ({
            ...day,
            day: `${index + 1}`
          }))
      }
    }));

    setSelectedDayIndex(prev => {
      if (prev === dayIndex) return 0; // reset to first day if the current one is removed
      if (prev > dayIndex) return prev - 1; // shift left when an earlier day is removed
      return prev; // no change needed
    });
  };

  const addTask = (dayIndex: number) => {
    const newTask: DailyTask = {
      name: '',
      description: '',
      placeRefId: ''
    };
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
          index === dayIndex
            ? { ...day, tasks: [...day.tasks, newTask] }
            : day
        )
      }
    }));
  };

  const removeTask = (dayIndex: number, taskIndex: number) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
          index === dayIndex
            ? { ...day, tasks: day.tasks.filter((_, tIndex) => tIndex !== taskIndex) }
            : day
        )
      }
    }));
  };

  const updateDay = (dayIndex: number, field: keyof DailyTask, value: any, taskIndex: number = 0) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
          index === dayIndex
            ? {
              ...day,
              tasks: day.tasks.map((task, tIndex) =>
                tIndex === taskIndex ? { ...task, [field]: value } : task
              )
            }
            : day
        )
      }
    }));
  };

  const updateBatch = (index: number, field: keyof Batch, value: any) => {
    setFormData(prev => ({
      ...prev,
      batches: prev.batches.map((batch, i) =>
        i === index ? { ...batch, [field]: value } : batch
      )
    }));
  };

  const addBatch = () => {
    const newBatch: Batch = {
      from: '',
      to: '',
      groupSize: 0,
      maxGroupSizeAllowed: 0
    };
    setFormData(prev => ({
      ...prev,
      batches: [...prev.batches, newBatch]
    }));
  };

  const removeBatch = (index: number) => {
    setFormData(prev => ({
      ...prev,
      batches: prev.batches.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple submissions

    // Clear any previous error
    setSubmitError(null);

    const hasValidationError = validateTripForm();
    if (hasValidationError) {
      setSubmitError('Please fix the highlighted validation errors before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const mediaFiles = imagesData;

      // Items with new File need signed URL upload; items with fileKey only (e.g. from copy) reuse existing
      const itemsWithFile = mediaFiles.filter((item: any) => item.file);
      const itemsWithExistingKey = mediaFiles.filter((item: any) => !item.file && item.fileKey);

      let uploadedFiles: (TripFile | null)[] = [];
      if (itemsWithFile.length > 0) {
        const filesProp = itemsWithFile.map((item: any) => ({
          folder: formData.type === 'Trip' ? 'trip' : 'itinerary',
          fileType: String(item.file.type),
        }));
        const signedUrlsData = await getSignedUploadUrls(filesProp);
        const signedFiles = signedUrlsData?.data?.files || [];
        if (signedFiles.length !== itemsWithFile.length) {
          setSubmitError('Could not obtain signed URLs for all files.');
          setIsSubmitting(false);
          return;
        }
        uploadedFiles = await Promise.all(itemsWithFile.map(async (item: any, index: number) => {
          try {
            const { uploadUrl, fileKey } = signedFiles[index];
            if (!uploadUrl || !fileKey || !item.file) return null;
            const uploadResponse = await Promise.race([
              uploadFileToSignedUrl(item.file, uploadUrl),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Upload timeout')), 30000)
              )
            ]);
            if (uploadResponse.status === 200) {
              return {
                fileKey,
                fileType: String(item.file.type),
                featured: index === 0,
              } as TripFile;
            }
            return null;
          } catch (error) {
            console.error('Upload error for file:', item.file?.name, error);
            return null;
          }
        }));
      }

      // Build validFiles in imagesData order: use existing fileKey when no File, else use upload result
      let uploadIndex = 0;
      const validFiles: TripFile[] = mediaFiles
        .map((item: any, index: number) => {
          if (item.file) {
            const uploaded = uploadedFiles[uploadIndex++];
            return uploaded ? { ...uploaded, featured: index === 0 ? true : uploaded.featured } : null;
          }
          if (item.fileKey) {
            return {
              fileKey: item.fileKey,
              fileType: item.fileType || 'image/jpeg',
              featured: item.featured ?? index === 0,
            } as TripFile;
          }
          return null;
        })
        .filter(Boolean) as TripFile[];

      if (validFiles.length === 0) {
        setSubmitError('At least one image is required.');
        setIsSubmitting(false);
        return;
      }

      const failedUploads = uploadedFiles.filter(f => f === null).length;
      if (failedUploads > 0 && itemsWithFile.length > 0) {
        console.warn(`${failedUploads} file(s) failed to upload, but ${validFiles.length} image(s) will be sent.`);
      }

      /**
       * Sanitize schedule tasks - mutually exclusive keys:
       * If placeRefId exists → Remove name completely
       * If name exists → Remove placeRefId completely
       * Never send both keys together
       */
      const sanitizedSchedule: Schedule = {
        dailySchedule: formData.schedule.dailySchedule.map(day => ({
          ...day,
          tasks: day.tasks.map(task => {
            const { name, description, placeRefId, file } = task as any;
            const sanitizedTask: any = { description };

            // Add file if present
            if (file) sanitizedTask.file = file;

            // If placeRefId exists, ONLY include placeRefId (remove name)
            if (placeRefId && placeRefId.trim().length > 0) {
              sanitizedTask.placeRefId = placeRefId;
              // Don't add name property at all
            }
            // If no placeRefId but name exists, ONLY include name (remove placeRefId)
            else if (name && name.trim().length > 0) {
              sanitizedTask.name = name;
              // Don't add placeRefId property at all
            }
            // If neither, at least have description

            return sanitizedTask;
          }),
        })),
      };

      // Determine the endpoint based on the type
      const endpoint = type === 'Trip' ? '/trips' : '/itineraries';

      // Create mode - send all data
      const requestBody = {
        ...formData,
        schedule: sanitizedSchedule,
        files: validFiles,
        pricePerPerson: rupeesToPaise(formData.pricePerPerson), // Convert rupees to paise
      };

      const response = await request<any, any>(endpoint, {
        method: 'POST',
        body: requestBody,
        authorization: true,
      });

      alert(`${type} created successfully`);
      const tripId = response?.data?._id || response?._id;
      console.log(`Created ${formData.type}:`, response);
      if (tripId) {
        navigate(`/trips/${tripId}?type=${type.toLowerCase()}`, {
          replace: true,
          state: { type: type.toLowerCase() }
        });
      } else {
        navigate('/trips', { replace: true });
      }
    } catch (err: any) {
      // Extract error data from backend
      const errorData = err?.errorData || err?.response || {};
      const validationErrors = err?.errors || errorData?.errors || [];

      // Format validation errors for display
      let errorMessage = 'Failed to create trip/itinerary. Please try again.';

      if (validationErrors.length > 0) {
        // Format validation errors for display
        const errorMessages = validationErrors.map((validationErr: any) => {
          // Convert field paths to readable names
          let fieldName = validationErr.path || '';

          // Make field names more readable
          if (fieldName.includes('dailySchedule')) {
            const dayMatch = fieldName.match(/dailySchedule\[(\d+)\]/);
            const taskMatch = fieldName.match(/tasks\[(\d+)\]/);
            if (dayMatch && taskMatch) {
              const dayNum = parseInt(dayMatch[1]) + 1;
              const taskNum = parseInt(taskMatch[1]) + 1;
              fieldName = `Day ${dayNum}, Task ${taskNum}`;
            } else if (dayMatch) {
              const dayNum = parseInt(dayMatch[1]) + 1;
              fieldName = `Day ${dayNum}`;
            }
          } else if (fieldName.includes('batches')) {
            const batchMatch = fieldName.match(/batches\[(\d+)\]/);
            if (batchMatch) {
              const batchNum = parseInt(batchMatch[1]) + 1;
              const fieldPart = fieldName.split('.').pop() || '';
              fieldName = `Batch ${batchNum} - ${fieldPart === 'from' ? 'Start Date' : fieldPart === 'to' ? 'End Date' : fieldPart}`;
            }
          } else {
            // Capitalize first letter and replace camelCase with spaces
            fieldName = fieldName
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str: string) => str.toUpperCase())
              .trim();
          }

          return `• ${fieldName}: ${validationErr.msg || validationErr.message || 'Invalid value'}`;
        });

        errorMessage = `Validation failed:\n\n${errorMessages.join('\n')}`;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      // Set error state to show user
      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateTrip = async () => {
    if (isSubmitting) return; // Prevent multiple submissions
    if (!editingTrip || !originalTripData) {
      alert('No trip data available for update');
      return;
    }

    // Clear any previous error
    setSubmitError(null);

    const hasValidationError = validateTripForm();
    if (hasValidationError) {
      setSubmitError('Please fix the highlighted validation errors before updating.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Helper function to normalize schedule for comparison (remove _id fields)
      const normalizeSchedule = (schedule: any) => {
        if (!schedule || !schedule.dailySchedule) return { dailySchedule: [] };
        return {
          dailySchedule: schedule.dailySchedule.map((day: any) => ({
            day: day.day,
            tasks: (day.tasks || []).map((task: any) => {
              const normalized: any = { description: task.description || '' };
              if (task.placeRefId) normalized.placeRefId = task.placeRefId;
              if (task.name) normalized.name = task.name;
              if (task.file) normalized.file = task.file;
              return normalized;
            })
          }))
        };
      };

      // Helper function to normalize batches for comparison (remove _id fields)
      const normalizeBatches = (batches: any[]) => {
        return (batches || []).map((batch: any) => ({
          from: batch.from,
          to: batch.to,
          groupSize: batch.groupSize || 0,
          maxGroupSizeAllowed: batch.maxGroupSizeAllowed || 0
        }));
      };

      // Handle image upload - only upload if images were changed (have File objects)
      const originalFiles = originalTripData.files || [];
      let validFiles: TripFile[] = originalFiles; // Default to original files

      // Check if images were changed (have File objects)
      const mediaFilesWithFiles = imagesData.filter((item: any) => item.file);

      if (mediaFilesWithFiles.length > 0) {
        // Images were changed - upload new files
        // Backend will validate file types and sizes
        const validMediaFiles = mediaFilesWithFiles.filter((item: any) => item.file);

        // Prepare file upload request payload
        const filesProp = validMediaFiles.map((item: any) => ({
          folder: formData.type === 'Trip' ? 'trip' : 'itinerary',
          fileType: String(item.file.type),
        }));

        const signedUrlsData = await getSignedUploadUrls(filesProp);
        const signedFiles = signedUrlsData?.data?.files || [];

        if (signedFiles.length !== validMediaFiles.length) {
          setSubmitError('Could not obtain signed URLs for all files.');
          setIsSubmitting(false);
          return;
        }

        const uploadedFiles = await Promise.all(validMediaFiles.map(async (item: any, index: number) => {
          try {
            const { uploadUrl, fileKey } = signedFiles[index];
            if (!uploadUrl || !fileKey) {
              console.error('Missing upload URL or file key for index:', index);
              return null;
            }

            if (!item.file) {
              console.error('File object missing for item at index:', index);
              return null;
            }

            // Perform the upload with timeout
            const uploadResponse = await Promise.race([
              uploadFileToSignedUrl(item.file, uploadUrl),
              new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Upload timeout')), 30000)
              )
            ]);

            if (uploadResponse.status === 200) {
              return {
                fileKey,
                fileType: String(item.file.type),
                featured: index === 0,
              } as TripFile;
            } else {
              console.error('Upload failed with status:', uploadResponse.status);
              return null;
            }
          } catch (error) {
            console.error('Upload error for file:', item.file?.name, error);
            return null;
          }
        }));

        validFiles = uploadedFiles.filter(Boolean) as TripFile[];
        const failedUploads = uploadedFiles.filter(file => file === null).length;

        if (validFiles.length === 0) {
          setSubmitError('File upload failed. Please check your internet connection and try again.');
          setIsSubmitting(false);
          return;
        }

        if (failedUploads > 0) {
          console.warn(`${failedUploads} files failed to upload, but ${validFiles.length} files uploaded successfully.`);
        }
      }

      // Sanitize schedule tasks
      const sanitizedSchedule: Schedule = {
        dailySchedule: formData.schedule.dailySchedule.map(day => ({
          ...day,
          tasks: day.tasks.map(task => {
            const { name, description, placeRefId, file } = task as any;
            const sanitizedTask: any = { description };

            if (file) sanitizedTask.file = file;

            if (placeRefId && placeRefId.trim().length > 0) {
              sanitizedTask.placeRefId = placeRefId;
            } else if (name && name.trim().length > 0) {
              sanitizedTask.name = name;
            }

            return sanitizedTask;
          }),
        })),
      };

      // Compare current data with original to find changed fields
      const changedFields: any = {};

      // Compare simple fields
      if (formData.title !== originalTripData.title) {
        changedFields.title = formData.title;
      }
      if (formData.description !== originalTripData.description) {
        changedFields.description = formData.description;
      }
      if (formData.notes !== originalTripData.notes) {
        changedFields.notes = formData.notes;
      }

      // Compare price (convert to paise for comparison)
      const currentPriceInPaise = rupeesToPaise(formData.pricePerPerson);
      if (currentPriceInPaise !== originalTripData.pricePerPerson) {
        changedFields.pricePerPerson = currentPriceInPaise;
      }

      // Compare categories
      const originalCategories = (originalTripData.category || []).sort();
      const currentCategories = (formData.category || []).sort();
      if (JSON.stringify(currentCategories) !== JSON.stringify(originalCategories)) {
        changedFields.category = formData.category;
      }

      // Compare location
      const originalLoc = originalTripData.location;
      const currentLoc = formData.location;
      if (!originalLoc || !currentLoc ||
        originalLoc.lat !== currentLoc.lat ||
        originalLoc.lng !== currentLoc.lng ||
        originalLoc.name !== currentLoc.name) {
        changedFields.location = formData.location;
      }

      // Compare schedule (normalize both before comparison)
      const normalizedOriginalSchedule = normalizeSchedule(originalTripData.schedule);
      const normalizedCurrentSchedule = normalizeSchedule(sanitizedSchedule);
      if (JSON.stringify(normalizedCurrentSchedule) !== JSON.stringify(normalizedOriginalSchedule)) {
        changedFields.schedule = sanitizedSchedule;
      }

      // Compare batches (normalize both before comparison)
      const normalizedOriginalBatches = normalizeBatches(originalTripData.batches || []);
      const normalizedCurrentBatches = normalizeBatches(formData.batches || []);
      if (JSON.stringify(normalizedCurrentBatches) !== JSON.stringify(normalizedOriginalBatches)) {
        changedFields.batches = formData.batches;
      }

      // Compare files - check if files changed
      const originalFileKeys = (originalTripData.files || []).map((f: any) => f.fileKey).filter(Boolean).sort();
      const currentFileKeys = validFiles.map((f: TripFile) => f.fileKey).filter(Boolean).sort();

      // Check if files changed (different keys)
      const filesChanged = JSON.stringify(originalFileKeys) !== JSON.stringify(currentFileKeys);

      if (filesChanged) {
        changedFields.files = validFiles;
        console.log('Files changed - Original:', originalFileKeys, 'Current:', currentFileKeys);
      }

      // Only proceed if there are changes
      if (Object.keys(changedFields).length === 0) {
        setSubmitError('No changes detected');
        setIsSubmitting(false);
        return;
      }

      // Determine the endpoint based on the type
      const endpoint = type === 'Trip' ? '/trips' : '/itineraries';

      console.log('Changed fields to be sent:', changedFields);
      console.log('Original trip data (for reference):', originalTripData);

      const response = await request<any, any>(
        `${endpoint}/${editingTrip._id}`,
        {
          method: 'PUT',
          body: changedFields,
          authorization: true,
        }
      );

      alert(`${type} updated successfully`);
      const tripId = response?.data?._id || response?._id || editingTrip?._id;
      console.log(`Updated ${formData.type}:`, response);
      if (tripId) {
        navigate(`/trips/${tripId}?type=${type.toLowerCase()}`, {
          replace: true,
          state: { type: type.toLowerCase() }
        });
      } else {
        navigate('/trips', { replace: true });
      }
    } catch (err: any) {
      // Extract error data
      const errorData = err?.errorData || err?.response || {};
      const validationErrors = err?.errors || errorData?.errors || [];

      // Format validation errors for display
      let errorMessage = 'Failed to update trip/itinerary. Please try again.';

      if (validationErrors.length > 0) {
        const errorMessages = validationErrors.map((validationErr: any) => {
          let fieldName = validationErr.path || '';

          if (fieldName.includes('dailySchedule')) {
            const dayMatch = fieldName.match(/dailySchedule\[(\d+)\]/);
            const taskMatch = fieldName.match(/tasks\[(\d+)\]/);
            if (dayMatch && taskMatch) {
              const dayNum = parseInt(dayMatch[1]) + 1;
              const taskNum = parseInt(taskMatch[1]) + 1;
              fieldName = `Day ${dayNum}, Task ${taskNum}`;
            } else if (dayMatch) {
              const dayNum = parseInt(dayMatch[1]) + 1;
              fieldName = `Day ${dayNum}`;
            }
          } else if (fieldName.includes('batches')) {
            const batchMatch = fieldName.match(/batches\[(\d+)\]/);
            if (batchMatch) {
              const batchNum = parseInt(batchMatch[1]) + 1;
              const fieldPart = fieldName.split('.').pop() || '';
              fieldName = `Batch ${batchNum} - ${fieldPart === 'from' ? 'Start Date' : fieldPart === 'to' ? 'End Date' : fieldPart}`;
            }
          } else {
            fieldName = fieldName
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, (str: string) => str.toUpperCase())
              .trim();
          }

          return `• ${fieldName}: ${validationErr.msg || validationErr.message || 'Invalid value'}`;
        });

        errorMessage = `Validation failed:\n\n${errorMessages.join('\n')}`;
      } else if (errorData?.error) {
        errorMessage = errorData.error;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Draft handling functions
  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);

      // Convert File objects to base64 when saving
      const serializedImagesData = await Promise.all(imagesData.map(async img => ({
        url: img.url,
        caption: img.caption || '',
        featured: img.featured || false,
        fileBase64: img.file ? await fileToBase64(img.file) : null,
        fileName: img.file?.name || '',
        fileType: img.file?.type || '',
        fileSize: img.file?.size || 0
      })));

      const draftData = {
        type: formData.type,
        title: formData.title,
        description: formData.description,
        schedule: formData.schedule,
        pricePerPerson: formData.pricePerPerson,
        batches: formData.batches,
        files: formData.files,
        notes: formData.notes,
        category: formData.category,
        location: formData.location,
        imagesData: serializedImagesData,
        videosData: [], // No videos in current implementation
        author: {
          _id: user?._id || '',
          username: user?.username || '',
        },
      };

      if (currentDraftId) {
        tripDraftUtils.updateDraft(dispatch, currentDraftId, draftData as any);
        alert('Draft updated successfully!');
      } else {
        tripDraftUtils.saveDraft(dispatch, draftData as any);
        setCurrentDraftId(Date.now().toString());
        alert('Draft saved successfully!');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleLoadDraft = (draftId: string) => {
    const draft = tripDrafts.find((d: TripDraft) => d.id === draftId);
    if (draft) {
      setFormData({
        type: draft.type,
        title: draft.title,
        description: draft.description,
        schedule: draft.schedule,
        pricePerPerson: draft.pricePerPerson,
        batches: draft.batches,
        files: draft.files,
        notes: draft.notes,
        category: draft.category,
        location: draft.location,
      });

      // Convert base64 images back to preview format
      console.log('handleLoadDraft - Loading images:', draft.imagesData);
      const restoredImages = draft.imagesData.map((img: any) => {
        const newFile = img.fileBase64 ? base64ToFile(img.fileBase64, img.fileName, img.fileType) : null;
        const restoredImage = {
          url: newFile ? URL.createObjectURL(newFile) : img.url,
          caption: img.caption || '',
          featured: img.featured || false,
          file: newFile || undefined,
        };
        console.log('handleLoadDraft - Restored image:', restoredImage);
        return restoredImage;
      });
      setImagesData(restoredImages);
      // Force MediaUpload component to re-render with new data
      setMediaUploadKey(prev => prev + 1);
      console.log('handleLoadDraft - Set imagesData and updated mediaUploadKey');

      setCurrentDraftId(draftId);
      setSelectedCategories(draft.category);
    }
  };

  return (


    <Container sx={{ py: 3, backgroundColor: 'white' }}>
      <Box sx={{ borderRadius: 2 }}>

        {/* Header with Draft Buttons */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ color: 'black' }}>
              {editingTrip ? `Update ${formData.type}` : `Create ${formData.type}`}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                onClick={() => navigate('/trip/drafts')}>
                <DraftIcon />
                <Typography fontWeight={"medium"} sx={{ color: '#008387', textTransform: 'none' }}>{'Open from Draft'}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Media Upload Section */}
        <Box sx={{ mb: 3 }}>
          <MediaUpload
            key={mediaUploadKey}
            onImagesChange={handleFilesChange}
            mediaMode="image-only"
            maxImages={5}
            value={imagesData}
            type={'image'}
          />
        </Box>

        {/* Trip Type Toggle */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Create
          </Typography>
          {lockTripTypeForCopy && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Trip type matches the item you copied and cannot be changed.
            </Typography>
          )}
          <ToggleButtonGroup
            value={formData.type || type}
            exclusive
            disabled={lockTripTypeForCopy}
            onChange={(_, value) => {
              if (lockTripTypeForCopy || !value) return;
              handleInputChange('type', value);
            }}
            sx={{
              mb: 2,
              '& .MuiToggleButton-root': {
                borderRadius: '20px !important',
                px: 3,
                border: '1px solid #FF6B35',
                color: 'text.primary',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 53, 0.1)',
                },
                '&.Mui-selected': {
                  backgroundColor: '#FF6B35 !important',
                  color: 'white !important',
                  '&:hover': {
                    backgroundColor: '#E55A2B !important',
                  }
                }
              }
            }}
          >
            <ToggleButton onClick={() => { setType('Trip') }} value="Trip" sx={{ mx: 1 }}>
              Trip
            </ToggleButton>
            <ToggleButton onClick={() => { setType('Itinerary') }} value="Itinerary" sx={{ mx: 1 }}>
              Itinerary
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Basic Information */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label={type === 'Trip' ? "Trip title" : "Itinerary title"}
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            onBlur={() => markFieldTouched('title')}
            sx={{ mb: 1 }}
            required
            error={shouldShowError('title') && (!formData.title.trim() || formData.title.trim().length > 100)}
            helperText={shouldShowError('title') && !formData.title.trim()
              ? 'Title is required'
              : shouldShowError('title') && formData.title.trim().length > 100
                ? 'Title must be 100 characters or less'
                : ''}
            inputProps={{ maxLength: 100 }}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label={"Description"}
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            onBlur={() => markFieldTouched('description')}
            sx={{ mb: 1 }}
            required
            error={shouldShowError('description') && !formData.description.trim()}
            helperText={shouldShowError('description') && !formData.description.trim() ? 'Description is required' : ''}
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label={type === 'Trip' ? "Trip notes (optional)" : "Itinerary notes (optional)"}
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            sx={{ mb: 1 }}
          />
        </Box>

        {/* Categories */}
        <Box sx={{ mb: 3 }} onClick={() => markFieldTouched('category')}>
          {/* <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Categories *
          </Typography> */}
          <CategoryChips
          isMandatory
            categories={availableCategories}
            selectedCategories={selectedCategories}
            onChange={(cats) => {
              handleCategoriesChange(cats);
              markFieldTouched('category');
            }}
          />
          {shouldShowError('category') && (!formData.category || formData.category.length === 0) && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              Please select at least one category
            </Typography>
          )}
        </Box>

        {/* Location */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
            Location *
          </Typography>
          <AutoCompletePlaces
            key={formData.location?.lat ?? 'no-location'}
            value={{
              name: formData.location?.name,
              lat: formData.location?.lat || 0,
              lng: formData.location?.lng || 0,
            }}
            onPlaceSelect={(place) => {
              markFieldTouched('location');
              if (!place) {
                handleLocationSelect(null);
                return;
              }
              handleLocationSelect({
                lat: place.lat,
                lng: place.lng,
                address: place.name,
              });
            }}
            label="Location"
            placeholder="Enter a location"
          />
          {shouldShowError('location') && (!formData.location || !formData.location.name) && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              Location is required
            </Typography>
          )}
        </Box>

        {/* Price - Only show for Trips */}
        {/* {type === 'Trip' && ( */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label={type === 'Trip' ? "Price per person" : "Budget for itinerary"}
            value={formData.pricePerPerson === 0 ? '' : formData.pricePerPerson}
            onChange={(e) => {
              const { value } = e.target;
              if (value === '') {
                handleInputChange('pricePerPerson', 0);
                return;
              }

              if (/^\d+$/.test(value)) {
                handleInputChange('pricePerPerson', Number(value));
              }
            }}
            onBlur={() => markFieldTouched('pricePerPerson')}
            sx={{ mb: 2 }}
            required
            error={shouldShowError('pricePerPerson') && (!formData.pricePerPerson || formData.pricePerPerson <= 0)}
            helperText={shouldShowError('pricePerPerson') && (!formData.pricePerPerson || formData.pricePerPerson <= 0) ? 'Price must be greater than 0' : ''}
            inputProps={{ min: 1 }}
          />
        </Box>
        {/* )} */}

        {/* Batch Information */}
        {type === 'Trip' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Create batches
            </Typography>
            {formData.batches.map((batch, batchIndex) => (
              <Box key={batchIndex} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>

                  <Typography sx={{ mb: 2, color: 'text.primary', fontSize: '16px' }}>{`Batch ${batchIndex + 1}`}</Typography>

                  {formData.batches.length > 1 && (
                    <IconButton
                    disabled={isEditMode}
                      size="small"
                      onClick={() => removeBatch(batchIndex)}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                <Box >
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      disabled={isEditMode}
                      value={batch.from.split('T')[0] || ''}
                      onChange={(e) => updateBatch(batchIndex, 'from', `${e.target.value}T00:00:00.000Z`)}
                      onBlur={() => markFieldTouched(`batch_${batchIndex}_from`)}
                      sx={{ mb: 2 }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      required
                      error={shouldShowError(`batch_${batchIndex}_from`, isEditMode) && (!batch.from || new Date(batch.from).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0))}
                      helperText={shouldShowError(`batch_${batchIndex}_from`, isEditMode) && (!batch.from || new Date(batch.from).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0)) ? 'Start date cannot be older than today' : ''}
                    />
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      disabled={isEditMode}
                      value={batch.to.split('T')[0] || ''}
                      onChange={(e) => updateBatch(batchIndex, 'to', `${e.target.value}T00:00:00.000Z`)}
                      onBlur={() => markFieldTouched(`batch_${batchIndex}_to`)}
                      sx={{ mb: 2 }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: batch.from ? batch.from.split('T')[0] : new Date().toISOString().split('T')[0] }}
                      required
                      error={shouldShowError(`batch_${batchIndex}_to`, isEditMode) && (!batch.to || (Boolean(batch.from) && new Date(batch.to).setHours(0, 0, 0, 0) < new Date(batch.from).setHours(0, 0, 0, 0)))}
                      helperText={shouldShowError(`batch_${batchIndex}_to`, isEditMode) && !batch.to
                        ? 'End date is required'
                        : shouldShowError(`batch_${batchIndex}_to`, isEditMode) && Boolean(batch.from) && new Date(batch.to).setHours(0, 0, 0, 0) < new Date(batch.from).setHours(0, 0, 0, 0)
                          ? 'End date cannot be earlier than start date'
                          : ''}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', alignItems: 'center' }}>

                    <TextField
                      fullWidth
                      type="number"
                      disabled={isEditMode}
                      label="Max Group Size Allowed"
                      value={batch.maxGroupSizeAllowed || ''}
                      onChange={(e) => updateBatch(batchIndex, 'maxGroupSizeAllowed', Number(e.target.value))}
                      onBlur={() => markFieldTouched(`batch_${batchIndex}_maxGroupSize`)}
                      sx={{ mt: 2 }}
                      required
                      error={shouldShowError(`batch_${batchIndex}_maxGroupSize`, isEditMode) && (!batch.maxGroupSizeAllowed || batch.maxGroupSizeAllowed <= 0 || batch.maxGroupSizeAllowed > 50)}
                      helperText={shouldShowError(`batch_${batchIndex}_maxGroupSize`, isEditMode) && (!batch.maxGroupSizeAllowed || batch.maxGroupSizeAllowed <= 0 || batch.maxGroupSizeAllowed > 50) ? 'Max group size must be between 1 and 50' : ''}
                      inputProps={{ min: 1, max: 50, step: 1 }}
                    />
                  </Box>
                </Box>
                {batchIndex < formData.batches.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}

            <Button
              disabled={isEditMode}
              variant="text"
              sx={{ color: '#FF6B35', textTransform: 'none' }}
              onClick={addBatch}
              startIcon={<AddIcon />}
            >
              Add more batches
            </Button>
          </Box>
        )}

        {/* Daily Schedule */}
        <Box sx={{ mb: 3, backgroundColor: '#fff8f5', borderTopLeftRadius: '24px', borderTopRightRadius: '24px', p: 2, border: '2px solid #eedcce', }}>
          {/* <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Daily Schedule
            </Typography> */}

          {/* Day Tabs */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {formData.schedule.dailySchedule.map((day, index) => (
              <Chip
                key={index}
                label={`Day ${day.day}`}
                clickable
                onClick={() => setSelectedDayIndex(index)}
                sx={{
                  backgroundColor: selectedDayIndex === index ? '#FF6B35 !important' : '#E0E0E0',
                  color: selectedDayIndex === index ? 'white !important' : 'text.primary',
                  fontWeight: 'bold'
                }}
              />
            ))}

            {/* Add / Remove Day Buttons */}
            <IconButton disabled={isEditMode} size="small" onClick={addDay}>
              <AddIcon />
            </IconButton>
            {formData.schedule.dailySchedule.length > 1 && (
              <IconButton disabled={isEditMode} size="small" onClick={() => removeDay(selectedDayIndex)}>
                <CloseIcon />
              </IconButton>
            )}
          </Box>

          {/* Tasks for selected day */}
          {formData.schedule.dailySchedule[selectedDayIndex]?.tasks.map((task, taskIndex) => (
            <Box key={taskIndex} sx={{ mb: 2, pl: 2, borderLeft: '3px solid #FF6B35' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                {formData.schedule.dailySchedule[selectedDayIndex].tasks.length > 1 && (
                  <IconButton
                    size="small"
                    onClick={() => removeTask(selectedDayIndex, taskIndex)}
                    sx={{ ml: 'auto' }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Autocomplete
                disablePortal
                sx={{ mb: 2 }}
                options={places}
                getOptionLabel={(option) => option.label || ''}
                loading={loadingPlaces}
                value={places.find((p) => p.placeRefId === task.placeRefId) || null}
                renderInput={(params) => <TextField {...params} label="Select Place (optional)" />}
                onChange={(event, value) => {
                  console.log('Selected place:', value);
                  markFieldTouched(`task_${selectedDayIndex}_${taskIndex}_name`);
                  if (value) {
                    // Place selected: Set placeRefId and remove name completely
                    setFormData(prev => ({
                      ...prev,
                      schedule: {
                        dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
                          index === selectedDayIndex
                            ? {
                              ...day,
                              tasks: day.tasks.map((t, tIndex) => {
                                if (tIndex === taskIndex) {
                                  const { name, ...taskWithoutName } = t;
                                  return { ...taskWithoutName, placeRefId: value.placeRefId } as DailyTask;
                                }
                                return t;
                              })
                            }
                            : day
                        )
                      }
                    }));
                  } else {
                    // Place cleared: Remove placeRefId
                    setFormData(prev => ({
                      ...prev,
                      schedule: {
                        dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
                          index === selectedDayIndex
                            ? {
                              ...day,
                              tasks: day.tasks.map((t, tIndex) => {
                                if (tIndex === taskIndex) {
                                  const { placeRefId, ...taskWithoutPlace } = t;
                                  return taskWithoutPlace;
                                }
                                return t;
                              })
                            }
                            : day
                        )
                      }
                    }));
                  }
                }}
              />

              <TextField
                fullWidth
                label="Title"
                value={task.name || ''}
                onChange={(e) => {
                  // Custom name entered: Set name and remove placeRefId completely
                  const customName = e.target.value;
                  setFormData(prev => ({
                    ...prev,
                    schedule: {
                      dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
                        index === selectedDayIndex
                          ? {
                            ...day,
                            tasks: day.tasks.map((t, tIndex) => {
                              if (tIndex === taskIndex) {
                                const { placeRefId, ...taskWithoutPlace } = t;
                                return { ...taskWithoutPlace, name: customName } as DailyTask;
                              }
                              return t;
                            })
                          }
                          : day
                      )
                    }
                  }));
                }}
                onBlur={() => markFieldTouched(`task_${selectedDayIndex}_${taskIndex}_name`)}
                sx={{ mb: 2 }}
                error={shouldShowError(`task_${selectedDayIndex}_${taskIndex}_name`) && !task.name && !task.placeRefId}
                helperText={shouldShowError(`task_${selectedDayIndex}_${taskIndex}_name`) && !task.name && !task.placeRefId ? 'Task name or place is required' : ''}
              />

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Task description"
                value={task.description || ''}
                onChange={(e) => updateDay(selectedDayIndex, 'description', e.target.value, taskIndex)}
                onBlur={() => markFieldTouched(`task_${selectedDayIndex}_${taskIndex}_description`)}
                sx={{ mb: 2 }}
                required
                error={shouldShowError(`task_${selectedDayIndex}_${taskIndex}_description`) && !task.description?.trim()}
                helperText={shouldShowError(`task_${selectedDayIndex}_${taskIndex}_description`) && !task.description?.trim() ? 'Task description is required' : ''}
              />
            </Box>
          ))}

          {/* Add more tasks button for selected day */}
          <Button
          disabled={isEditMode}
            variant="text"
            sx={{ color: '#FF6B35', textTransform: 'none', mb: 2 }}
            onClick={() => addTask(selectedDayIndex)}
            startIcon={<AddIcon />}
          >
            Add more tasks
          </Button>
        </Box>

        {/* Error Message */}
        {submitError && (
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: '#fff5f5',
              borderRadius: '8px',
              border: '1px solid #fed7d7',
            }}
          >
            <Typography
              sx={{
                color: '#e53e3e',
                fontSize: '14px',
                fontWeight: 'medium',
                whiteSpace: 'pre-line',
              }}
            >
              {submitError}
            </Typography>
          </Box>
        )}

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleSaveDraft}
            disabled={isSavingDraft || imagesData.length === 0}
            sx={{
              borderColor: '#FF6B35',
              color: '#FF6B35',
              py: 1.5,
              px: 4,
              borderRadius: '25px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          >
            {isSavingDraft ? 'Saving Draft…' : 'Save as Draft'}
          </Button>
          <Button
            variant="contained"
            onClick={editingTrip ? handleUpdateTrip : handleSubmit}
            disabled={isSubmitting || imagesData.length === 0}
            sx={{
              backgroundColor: '#FF6B35',
              py: 1.5,
              px: 6,
              borderRadius: '25px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 'bold',
              '&:hover': {
                backgroundColor: '#E55A2B',
              },
            }}
          >
            {isSubmitting
              ? (editingTrip ? 'Updating Trip...' : 'Creating Trip...')
              : (editingTrip ? 'Update Trip' : 'Submit')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default CreateTrip;